import {
  ContractInputLocation,
  ContractInputPractitioner,
} from '../context/types';

export interface PractitionerInstance extends ContractInputPractitioner {
  locationCity: string;
  locationKey: string;
}

export interface DedupedPractitioner {
  /** Stable identity. NPI when present; falls back to a location-scoped key for null-NPI rows. */
  identityKey: string;
  npi: string | null;
  name: string;
  /** All (location, practitioner) pairs that map to this individual. */
  instances: PractitionerInstance[];
}

const locationKey = (loc: ContractInputLocation): string =>
  `${loc.address.locality}|${loc.address.address_line_1}`;

export const dedupePractitioners = (
  locations: ContractInputLocation[],
): DedupedPractitioner[] => {
  const byNpi = new Map<string, DedupedPractitioner>();
  const nullRows: DedupedPractitioner[] = [];

  for (const loc of locations) {
    const lk = locationKey(loc);
    for (const p of loc.practitioners) {
      const inst: PractitionerInstance = {
        ...p,
        locationCity: loc.address.locality,
        locationKey: lk,
      };
      if (p.npi == null) {
        // Null-NPI rows can't be deduped server-side; render each as its own
        // skipped row, identity-keyed by (location, name).
        nullRows.push({
          identityKey: `null:${lk}|${p.name}`,
          npi: null,
          name: p.name,
          instances: [inst],
        });
        continue;
      }
      const existing = byNpi.get(p.npi);
      if (existing) {
        existing.instances.push(inst);
      } else {
        byNpi.set(p.npi, {
          identityKey: `npi:${p.npi}`,
          npi: p.npi,
          name: p.name,
          instances: [inst],
        });
      }
    }
  }

  return [...byNpi.values(), ...nullRows];
};
